<template>
  <div
    class="new-webapp-container"
    v-if="item">
    <v-stepper
      v-model="step"
      vertical>
      <v-stepper-step
        step="1"
        editable>
        {{ $t('serverModel.components.webAppCreationWizard.selectDevice', 'Select a device') }}
      </v-stepper-step>
      <v-stepper-content step="1">
        <div>
          <Device
            v-for="(device, index) in devices"
            :key="index"
            v-bind="device"
            @click.native="selectDevice(device)"
            :class="selectedDevice === device && 'selected'" />
        </div>
        <div class="stepper-commands">
          <v-spacer />
          <v-btn
            color="success"
            :disabled="!selectedDevice"
            @click="step = 2"
            small>
            {{ $t('serverModel.components.webAppCreationWizard.continue', 'Continue') }}
          </v-btn>
        </div>
      </v-stepper-content>

      <v-stepper-step
        :editable="!!selectedDevice"
        step="2">
        {{ $t('serverModel.components.webAppCreationWizard.fillInformation', 'Basic info for your project') }}
      </v-stepper-step>

      <v-stepper-content
        step="2">
        <we-detail-view
          class="detail"
          display-in="designer_step1"
          :item="item"
          :schema="schema"
          :edit-mode="true" />
        <WebAppAvatarEdition
          :item="item"
          :device="selectedDevice.id" />

        <div class="stepper-commands">
          <v-btn
            color="blue darken-1"
            flat
            @click="step--"
            small>
            {{ $t('serverModel.components.webAppCreationWizard.back', 'Back') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="success"
            :disabled="!item.label || !item.description || !item.project"
            @click="step = 3"
            small>
            {{ $t('serverModel.components.webAppCreationWizard.continue', 'Continue') }}
          </v-btn>
        </div>
      </v-stepper-content>

      <v-stepper-step
        :editable="!!(selectedDevice && item.label && item.description && item.project)"
        v-if="!initialized"
        step="3">
        {{ $t('serverModel.components.webAppCreationWizard.fillCollaboration', 'Info for your Web Application') }}
      </v-stepper-step>

      <v-stepper-content
        step="3"
        v-if="!initialized">
        <we-detail-view
          class="detail"
          display-in="designer_step2"
          :item="item"
          :schema="schema"
          :edit-mode="true" />

        <div class="stepper-commands">
          <v-btn
            color="blue darken-1"
            flat
            @click="step--"
            small>
            {{ $t('serverModel.components.webAppCreationWizard.back', 'Back') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="success"
            @click="step = 4"
            small>
            {{ $t('serverModel.components.webAppCreationWizard.continue', 'Continue') }}
          </v-btn>
        </div>
      </v-stepper-content>

      <v-stepper-step
        :editable="!!(selectedDevice && item.label && item.description && item.project)"
        :step="lastStepId">
        {{ $t('serverModel.components.webAppCreationWizard.selectTemplate', 'Select your template') }}
      </v-stepper-step>
      <v-stepper-content :step="lastStepId">
        <div class="filter-panel">
          <div style="padding-top: 15px">
            <v-hover>
              <div slot-scope="{ hover }">
                <v-text-field
                  v-if="hover"
                  class="quick-search"
                  :placeholder="$t('serverModel.components.webAppCreationWizard.quickSearch','Quick search')"
                  v-model="search"
                  :append-icon="search ? '' : 'search'"
                  solo
                  clearable
                  @click:clear="clearSearch" />
                <v-icon :color="search && 'primary'">
                  {{ $t('serverModel.components.webAppCreationWizard.search','Search') }}
                </v-icon>
              </div>
            </v-hover>
          </div>
          <select-item
            class="selected-project"
            :data-source="projectDataSource"
            :value.sync="selectedProject" />
        </div>
        <div>
          <div class="webapp-tiles">
            <WebAppCard
              class="webapp-card"
              v-for="webApp in webApps"
              :key="webApp.id"
              :item="webApp"
              :selected="item.template === webApp"
              :can-like="false"
              @click.native="item.template = webApp">
              <template slot="commands">
                <div />
              </template>
            </WebAppCard>
          </div>
          <div
            class="text-center pagination">
            <v-pagination
              v-if="pagesLength > 1"
              total-visible="5"
              v-model="page"
              :length="pagesLength"
              circle
            />
          </div>
        </div>
        <div class="stepper-commands">
          <v-btn
            color="blue darken-1"
            flat
            @click="step--"
            small>
            {{ $t('serverModel.components.webAppCreationWizard.back', 'Back') }}
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="!standalone"
            color="success"
            :loading="saving"
            :disabled="!canSave"
            @click="save"
            small>
            {{ $t('serverModel.components.webAppCreationWizard.save', 'Save') }}
          </v-btn>
        </div>
      </v-stepper-content>
    </v-stepper>
  </div>
</template>

<script>
import { webAppWizardMixin } from '../mixins/webAppWizard'
import Device from './Device'
import '../assets/webAppWizard.scss'
import WebAppAvatarEdition from '../components/WebAppAvatarEdition'
import { DESIGN_ENVIRONMENT_DTO } from '../constants/environment'
import { webAppDataSource, projectDataSource } from '../services'
import WebAppCard from './WebAppCard'
import SelectItem from 'schema-view/src/components/Fields/NavigationProperty/SelectItem'
import { debounce } from 'lodash'

export default {
  mixins: [webAppWizardMixin],
  components: { WebAppAvatarEdition, Device, WebAppCard, SelectItem },
  data () {
    return {
      webApps: [],
      search: '',
      debouncedSearch: '',
      schema: webAppDataSource.schema,
      selectedProject: null,
      projectDataSource: projectDataSource,
      page: 1,
      size: 5,
      total: 0,
      defaultTemplatesWebapp: [
        {
          id: '',
          name: 'blank',
          label: this.$t('serverModel.components.webAppCreationWizard.blank', 'Blank'),
          createdOn: '2020-01-31T17:15:33.285746Z',
          ownerId: 1,
          owner: {
            id: 1,
            login: 'administrator'
          }
        }
      ]
    }
  },
  asyncComputed: {
    webAppsRequest () {
      return this.requestOptions && webAppDataSource.summary(this.requestOptions)
    },
    projectsRequest () {
      return projectDataSource.list()
    }
  },
  computed: {
    requestOptions () {
      return this.getRequestOptions({
        page: this.page,
        quickSearch: this.debouncedSearch,
        environmentId: DESIGN_ENVIRONMENT_DTO.id,
        projectId: this.selectedProject && this.selectedProject.id
      })
    },
    projects () {
      return this.projectsRequest && this.projectsRequest.content
    },
    pagesLength () {
      return this.totalElement && Math.ceil(this.totalElement / this.size)
    },
    totalElement () {
      return this.webAppsRequest && this.webAppsRequest.totalElements
    },
    delayUpdateSearch () {
      return debounce(this.updateDebouncedSearch, 500)
    },
    lastStepId () {
      return this.initialized ? 3 : 4
    }
  },
  watch: {
    selectedProject () {
      this.page = 1
      this.item.template = null
      this.debouncedSearch = ''
      this.search = ''
    },
    projects: {
      deep: true,
      handler: function (newVal) {
        if (newVal[0]) {
          this.selectedProject = newVal[0]
        }
      }
    },
    search: {
      immediate: true,
      handler () {
        this.delayUpdateSearch && this.delayUpdateSearch()
      }
    },
    'webAppsRequest.content' (content) {
      let webapps = content || []
      if (!this.search) {
        webapps = [
          ...this.defaultTemplatesWebapp,
          ...webapps
        ]
      }
      this.webApps = webapps
    }
  },
  methods: {
    getRequestOptions ({ page, quickSearch, environmentId, projectId }) {
      if (!page) {
        throw new Error('page must be greater than 0')
      }
      return {
        page: page - 1,
        size: this.size,
        sort: 'updatedOn,desc',
        quickSearch: quickSearch || undefined,
        filter: [environmentId && `environment.id==${environmentId}`, projectId && `project.id==${projectId}`].filter(f => f).join(';')
      }
    },
    clearSearch () {
      this.search = ''
    },
    updateDebouncedSearch () {
      this.page = 1
      this.debouncedSearch = this.search
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "~common-assets/scss/we";
  .webapp-tiles {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    .webapp-card {
      display: grid;
      width: 50%;
    }
  }
  .pagination {
    text-align: center;
  }
  .selected-project{
    padding-right: 5px;
    width: 100%;
  }
  .filter-panel {
    @extend .flex-row;
    position: relative;
    .quick-search {
      max-width: 80%;
      position: absolute;
      top: 0;
      left: 25px;
      z-index: 9;
      /deep/ {
        .v-input__slot {
          border: 1px solid gray;
          max-height: 30px;
        }
      }
    }
  }
</style>
